/**
 * OCRPanel - Tableau de bord admin pour les scans OCR
 * Permet de visualiser tous les scans, voir les images, et réanalyser si besoin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';

const OCRPanel = ({ token, dateRange }) => {
  const { showSuccess, showError } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [scans, setScans] = useState([]);
  const [stats, setStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [users, setUsers] = useState([]); // Liste des utilisateurs pour filtre
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    engine: '',
    success: '',
    userId: '',
    validationStatus: '' // pending, validated, incorrect
  });
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanImageUrl, setScanImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(null);
  const [updatingValidation, setUpdatingValidation] = useState(null);
  const [activeTab, setActiveTab] = useState('scans');
  // Modal pour remarque "incorrect"
  const [noteModal, setNoteModal] = useState({ open: false, expenseId: null, note: '' });

  const loadScans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end,
        page: page.toString(),
        limit: '20'
      });

      if (filters.engine) params.append('engine', filters.engine);
      if (filters.success !== '') params.append('success', filters.success);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.validationStatus) params.append('validationStatus', filters.validationStatus);

      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/ocr/scans?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setScans(data.data.scans);
        setStats(data.data.stats);
        setTotalPages(data.data.totalPages);
      }
    } catch (err) {
      console.error('Erreur chargement scans OCR:', err);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, page, filters]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end
      });

      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/ocr/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setGlobalStats(data.data.global);
        setDailyTrend(data.data.dailyTrend);
        setUserStats(data.data.userStats);
      }
    } catch (err) {
      console.error('Erreur chargement stats OCR:', err);
    }
  }, [token, dateRange]);

  // Charger la liste des utilisateurs pour le filtre
  const loadUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err);
    }
  }, [token]);

  // Mettre à jour le statut de validation d'un scan
  const updateValidationStatus = async (expenseId, status, note = null) => {
    if (!expenseId) return;

    // Si "incorrect" sans note, ouvrir la modal
    if (status === 'incorrect' && !note) {
      setNoteModal({ open: true, expenseId, note: '' });
      return;
    }

    setUpdatingValidation(expenseId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/ocr/scan/${expenseId}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ validation_status: status, validation_note: note })
      });

      const data = await response.json();
      if (data.success) {
        showSuccess(`Scan marqué comme "${status === 'validated' ? 'validé' : status === 'incorrect' ? 'incorrect' : 'en attente'}"`);
        loadScans();
        setNoteModal({ open: false, expenseId: null, note: '' });
      } else {
        showError(data.error || 'Erreur mise à jour');
      }
    } catch (err) {
      showError('Erreur: ' + err.message);
    } finally {
      setUpdatingValidation(null);
    }
  };

  // Soumettre la remarque depuis la modal
  const submitIncorrectNote = () => {
    if (!noteModal.note.trim()) {
      showError('Veuillez saisir une remarque');
      return;
    }
    updateValidationStatus(noteModal.expenseId, 'incorrect', noteModal.note);
  };

  useEffect(() => {
    loadScans();
    loadStats();
    loadUsers();
  }, [loadScans, loadStats, loadUsers]);

  // Charger l'image quand on sélectionne un scan
  useEffect(() => {
    if (selectedScan?.expense_id) {
      setImageLoading(true);
      setScanImageUrl(null);
      fetch(`${API_BASE_URL}/api/analytics/dashboard/ocr/scan/${selectedScan.expense_id}/image`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => {
          if (!response.ok) throw new Error('Image non disponible');
          return response.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setScanImageUrl(url);
        })
        .catch(err => {
          console.error('Erreur chargement image:', err);
          setScanImageUrl(null);
        })
        .finally(() => setImageLoading(false));
    } else {
      setScanImageUrl(null);
    }

    // Cleanup: révoquer l'URL blob quand le composant est démonté ou le scan change
    return () => {
      if (scanImageUrl) {
        URL.revokeObjectURL(scanImageUrl);
      }
    };
  }, [selectedScan, token]);

  const handleReanalyze = async (expenseId) => {
    if (!expenseId) {
      showError('Pas de note de frais associee');
      return;
    }

    setReanalyzing(expenseId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/ocr/scan/${expenseId}/reanalyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        showSuccess('Reanalyse OCR terminee');
        loadScans();
      } else {
        showError(data.error || 'Erreur reanalyse');
      }
    } catch (err) {
      showError('Erreur reanalyse: ' + err.message);
    } finally {
      setReanalyzing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} Mo` : `${kb.toFixed(0)} Ko`;
  };

  const getSuccessRate = () => {
    if (!stats || !stats.total_scans) return 0;
    return Math.round((parseInt(stats.successful) / parseInt(stats.total_scans)) * 100);
  };

  return (
    <div>
      {/* Stats cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>Total Scans</div>
            <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.total_scans}</div>
          </div>
          <div style={{
            background: getSuccessRate() >= 80 ? '#e8f5e9' : '#fff3e0',
            padding: '20px',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '13px', color: '#666' }}>Taux de succes</div>
            <div style={{
              fontSize: '28px',
              fontWeight: '700',
              color: getSuccessRate() >= 80 ? '#2e7d32' : '#f57c00'
            }}>
              {getSuccessRate()}%
            </div>
          </div>
          <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#1565c0' }}>PaddleOCR</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1565c0' }}>
              {stats.paddleocr_count || 0}
            </div>
          </div>
          <div style={{ background: '#fce4ec', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#c2185b' }}>Tesseract</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#c2185b' }}>
              {stats.tesseract_count || 0}
            </div>
          </div>
          <div style={{ background: '#f3e8ff', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#7b1fa2' }}>HuggingFace</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#7b1fa2' }}>
              {stats.hf_count || 0}
            </div>
          </div>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#666' }}>Utilisateurs</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#333' }}>
              {stats.unique_users || 0}
            </div>
          </div>
          <div style={{ background: '#fff8e1', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#f57f17' }}>Temps moyen</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#f57f17' }}>
              {stats.avg_duration_ms ? `${(stats.avg_duration_ms / 1000).toFixed(1)}s` : '-'}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '1px solid #eee',
        paddingBottom: '12px'
      }}>
        {[
          { id: 'scans', label: 'Tous les scans' },
          { id: 'trend', label: 'Tendance' },
          { id: 'users', label: 'Par utilisateur' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              background: activeTab === tab.id ? '#1a1a2e' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content: Scans list */}
      {activeTab === 'scans' && (
        <>
          {/* Filtres */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
            background: '#f9fafb',
            padding: '16px',
            borderRadius: '10px'
          }}>
            {/* Filtre utilisateur */}
            <select
              value={filters.userId}
              onChange={(e) => { setFilters(f => ({ ...f, userId: e.target.value })); setPage(1); }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px',
                minWidth: '180px'
              }}
            >
              <option value="">Tous les utilisateurs</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>

            {/* Filtre moteur */}
            <select
              value={filters.engine}
              onChange={(e) => { setFilters(f => ({ ...f, engine: e.target.value })); setPage(1); }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px'
              }}
            >
              <option value="">Tous les moteurs</option>
              <option value="paddleocr">PaddleOCR</option>
              <option value="tesseract">Tesseract</option>
              <option value="hf_gemma-3n">Gemma 3n (HF)</option>
              <option value="hf_qwen3-vl">Qwen3-VL (HF)</option>
            </select>

            {/* Filtre succès */}
            <select
              value={filters.success}
              onChange={(e) => { setFilters(f => ({ ...f, success: e.target.value })); setPage(1); }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px'
              }}
            >
              <option value="">Succès/Echec</option>
              <option value="true">Succès</option>
              <option value="false">Echec</option>
            </select>

            {/* Filtre validation */}
            <select
              value={filters.validationStatus}
              onChange={(e) => { setFilters(f => ({ ...f, validationStatus: e.target.value })); setPage(1); }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px'
              }}
            >
              <option value="">Toutes validations</option>
              <option value="pending">En attente</option>
              <option value="validated">Validé</option>
              <option value="incorrect">Incorrect</option>
            </select>

            <button
              onClick={() => {
                setFilters({ engine: '', success: '', userId: '', validationStatus: '' });
                setPage(1);
              }}
              style={{
                padding: '8px 12px',
                background: '#e5e7eb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Réinitialiser
            </button>
          </div>

          {/* Liste des scans */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Chargement...
            </div>
          ) : scans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucun scan OCR trouve pour cette periode
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid #eee'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Date scan</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Utilisateur</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>OCR</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Données saisies</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>Validation</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Remarque</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((scan, idx) => {
                    const validationStatus = scan.ocr_validation_status || 'pending';
                    const validationColors = {
                      pending: { bg: '#fff3e0', color: '#f57c00', label: 'En attente' },
                      validated: { bg: '#e8f5e9', color: '#2e7d32', label: 'Validé' },
                      incorrect: { bg: '#ffebee', color: '#c62828', label: 'Incorrect' }
                    };
                    const vStatus = validationColors[validationStatus] || validationColors.pending;

                    return (
                      <tr
                        key={scan.event_id}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          background: idx % 2 === 0 ? 'white' : '#fafafa'
                        }}
                      >
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {formatDate(scan.created_at)}
                          {scan.expense_updated_at && new Date(scan.expense_updated_at) > new Date(scan.created_at) && (
                            <div style={{ fontSize: '10px', color: '#2196F3', marginTop: '2px' }}>
                              MAJ: {formatDate(scan.expense_updated_at)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          <div style={{ fontWeight: '500' }}>{scan.user_name || 'Inconnu'}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{scan.user_email}</div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500',
                              background: scan.success ? '#e8f5e9' : '#ffebee',
                              color: scan.success ? '#2e7d32' : '#c62828'
                            }}>
                              {scan.success ? 'OK' : 'Echec'}
                            </span>
                            <span style={{
                              fontSize: '10px',
                              color: scan.engine?.startsWith('hf_') ? '#7b1fa2'
                                : scan.engine?.startsWith('paddleocr') ? '#1565c0' : '#c2185b'
                            }}>
                              {scan.engine?.startsWith('hf_gemma') ? 'Gemma 3n'
                                : scan.engine?.startsWith('hf_qwen') ? 'Qwen3-VL'
                                : scan.engine?.startsWith('hf_') ? 'HuggingFace'
                                : scan.engine?.startsWith('paddleocr') ? 'PaddleOCR' : 'Tesseract'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {scan.expense_id ? (
                            <div style={{ fontSize: '12px' }}>
                              <div style={{ fontWeight: '600', color: '#1a1a2e' }}>
                                {scan.expense_amount ? `${parseFloat(scan.expense_amount).toFixed(2)} €` : '-'}
                              </div>
                              {scan.expense_merchant && (
                                <div style={{ color: '#666', marginTop: '2px' }}>
                                  {scan.expense_merchant}
                                </div>
                              )}
                              {scan.expense_date && (
                                <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
                                  {new Date(scan.expense_date).toLocaleDateString('fr-FR')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                              Non lié
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {scan.expense_id ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => updateValidationStatus(scan.expense_id, 'validated')}
                                disabled={updatingValidation === scan.expense_id}
                                title="Marquer comme validé"
                                style={{
                                  padding: '4px 6px',
                                  background: validationStatus === 'validated' ? '#2e7d32' : '#e8f5e9',
                                  color: validationStatus === 'validated' ? 'white' : '#2e7d32',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => updateValidationStatus(scan.expense_id, 'incorrect')}
                                disabled={updatingValidation === scan.expense_id}
                                title="Marquer comme incorrect"
                                style={{
                                  padding: '4px 6px',
                                  background: validationStatus === 'incorrect' ? '#c62828' : '#ffebee',
                                  color: validationStatus === 'incorrect' ? 'white' : '#c62828',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                ✗
                              </button>
                              <button
                                onClick={() => updateValidationStatus(scan.expense_id, 'pending')}
                                disabled={updatingValidation === scan.expense_id}
                                title="Remettre en attente"
                                style={{
                                  padding: '4px 6px',
                                  background: validationStatus === 'pending' ? '#f57c00' : '#fff3e0',
                                  color: validationStatus === 'pending' ? 'white' : '#f57c00',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                ?
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', maxWidth: '200px' }}>
                          {scan.ocr_validation_note ? (
                            <div style={{
                              fontSize: '11px',
                              color: '#c62828',
                              background: '#ffebee',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {scan.ocr_validation_note}
                            </div>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            {scan.expense_id ? (
                              <>
                                <button
                                  onClick={() => setSelectedScan(scan)}
                                  style={{
                                    padding: '4px 8px',
                                    background: '#1a1a2e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                  }}
                                  title="Voir détails et image"
                                >
                                  Voir
                                </button>
                                <button
                                  onClick={() => handleReanalyze(scan.expense_id)}
                                  disabled={reanalyzing === scan.expense_id}
                                  style={{
                                    padding: '4px 8px',
                                    background: reanalyzing === scan.expense_id ? '#ccc' : '#f5f5f5',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: reanalyzing === scan.expense_id ? 'not-allowed' : 'pointer',
                                    fontSize: '11px'
                                  }}
                                  title="Relancer OCR"
                                >
                                  {reanalyzing === scan.expense_id ? '...' : '↻'}
                                </button>
                              </>
                            ) : (
                              <span style={{ fontSize: '11px', color: '#999' }}>-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '20px'
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 16px',
                  background: page === 1 ? '#eee' : 'white',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Precedent
              </button>
              <span style={{
                padding: '8px 16px',
                background: '#f5f5f5',
                borderRadius: '6px'
              }}>
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '8px 16px',
                  background: page === totalPages ? '#eee' : 'white',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {/* Tab content: Daily trend */}
      {activeTab === 'trend' && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #eee'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600' }}>
            Evolution quotidienne des scans
          </h3>
          {dailyTrend.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucune donnee pour cette periode
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Total</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Succes</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>PaddleOCR</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Tesseract</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>HuggingFace</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyTrend.map((day, idx) => (
                    <tr key={day.date} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {new Date(day.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>
                        {day.scans}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#2e7d32' }}>
                        {day.successful}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#1565c0' }}>
                        {day.paddleocr}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#c2185b' }}>
                        {day.tesseract}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#7b1fa2' }}>
                        {day.hf || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: (day.successful / day.scans) >= 0.8 ? '#e8f5e9' : '#fff3e0',
                          color: (day.successful / day.scans) >= 0.8 ? '#2e7d32' : '#f57c00'
                        }}>
                          {Math.round((day.successful / day.scans) * 100)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab content: User stats */}
      {activeTab === 'users' && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #eee'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600' }}>
            Utilisation par utilisateur
          </h3>
          {userStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucune donnee pour cette periode
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px' }}>Utilisateur</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Scans</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Succes</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Taux</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Temps moyen</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {userStats.map((user, idx) => (
                    <tr key={user.user_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500', fontSize: '13px' }}>{user.user_name || 'Inconnu'}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{user.email}</div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>
                        {user.total_scans}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#2e7d32' }}>
                        {user.successful}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: (user.successful / user.total_scans) >= 0.8 ? '#e8f5e9' : '#fff3e0',
                          color: (user.successful / user.total_scans) >= 0.8 ? '#2e7d32' : '#f57c00'
                        }}>
                          {Math.round((user.successful / user.total_scans) * 100)}%
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>
                        {user.avg_duration_ms ? `${(user.avg_duration_ms / 1000).toFixed(1)}s` : '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>
                        {user.total_mb ? `${user.total_mb} Mo` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal visualisation image */}
      {selectedScan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Header modal */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>Detail du scan OCR</h3>
                <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                  {formatDate(selectedScan.created_at)} - {selectedScan.user_name}
                  {selectedScan.expense_updated_at && selectedScan.expense_updated_at !== selectedScan.created_at && (
                    <span style={{ marginLeft: '12px', color: '#2196F3' }}>
                      (MAJ: {formatDate(selectedScan.expense_updated_at)})
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  if (scanImageUrl) URL.revokeObjectURL(scanImageUrl);
                  setScanImageUrl(null);
                  setSelectedScan(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                x
              </button>
            </div>

            {/* Contenu modal */}
            <div style={{ padding: '20px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              {/* Image */}
              <div style={{ flex: '1 1 300px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Image du justificatif</h4>
                {imageLoading ? (
                  <div style={{
                    padding: '60px 40px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    Chargement de l'image...
                  </div>
                ) : scanImageUrl ? (
                  <img
                    src={scanImageUrl}
                    alt="Justificatif"
                    style={{
                      width: '100%',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #eee'
                    }}
                  />
                ) : (
                  <div style={{
                    padding: '60px 40px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#999'
                  }}>
                    {selectedScan.expense_id ? 'Erreur chargement image' : 'Image non disponible'}
                  </div>
                )}
              </div>

              {/* Infos */}
              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Informations</h4>
                <div style={{
                  background: '#f9fafb',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>Moteur OCR</div>
                    {(() => {
                      const eng = selectedScan.engine || '';
                      const isHF = eng.includes('qwen') || eng.includes('gemma');
                      const isPaddle = eng.startsWith('paddleocr');
                      const label = isHF ? eng.replace('hf_', '').replace('hf-', '').replace('qwen3-vl', 'Qwen3-VL').replace('gemma3n', 'Gemma 3n')
                        : isPaddle ? 'PaddleOCR' : 'Tesseract';
                      const bg = isHF ? '#f3e5f5' : isPaddle ? '#e3f2fd' : '#fce4ec';
                      const color = isHF ? '#7b1fa2' : isPaddle ? '#1565c0' : '#c2185b';
                      return (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: bg,
                          color: color
                        }}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>Statut</div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: selectedScan.success ? '#e8f5e9' : '#ffebee',
                      color: selectedScan.success ? '#2e7d32' : '#c62828'
                    }}>
                      {selectedScan.success ? 'Succes' : 'Echec'}
                    </span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>Duree</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {selectedScan.duration_ms ? `${(selectedScan.duration_ms / 1000).toFixed(2)} secondes` : '-'}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>Detections</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {(() => {
                        const ext = selectedScan.ocr_extracted_data?.extracted || selectedScan.ocr_extracted_data || selectedScan.ocr_parsed_json || {};
                        const count = [ext.amount, ext.merchant, ext.date, ext.city].filter(v => v != null && v !== '').length;
                        return `${count} champ${count > 1 ? 's' : ''}`;
                      })()}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>Fichier</div>
                    <div style={{ fontSize: '13px' }}>{selectedScan.filename || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{formatFileSize(selectedScan.filesize)}</div>
                  </div>
                </div>

                {/* Données saisies/validées */}
                {selectedScan.expense_id && (() => {
                  const diff = selectedScan.corrections_diff || {};
                  const correctedStyle = { fontWeight: '500', color: '#e65100', borderBottom: '2px solid #e65100' };
                  const normalStyle = { fontWeight: '500' };
                  const isCorrected = (field) => diff[field]?.corrected;
                  const correctedIcon = <span title="Modifié par l'utilisateur" style={{ marginLeft: '4px', fontSize: '10px', color: '#e65100' }}>✎</span>;
                  return (
                  <div style={{ marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>
                      Données saisies
                      {Object.values(diff).some(d => d?.corrected) && (
                        <span style={{ fontSize: '11px', color: '#e65100', marginLeft: '8px', fontWeight: '400' }}>
                          ✎ = modifié par l'utilisateur
                        </span>
                      )}
                    </h4>
                    <div style={{
                      background: '#e3f2fd',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Montant TTC: </span>
                        <span style={isCorrected('amount') ? correctedStyle : { fontWeight: '600', color: '#1565c0' }}>
                          {selectedScan.expense_amount ? `${parseFloat(selectedScan.expense_amount).toFixed(2)} €` : '-'}
                          {isCorrected('amount') && correctedIcon}
                        </span>
                      </div>
                      {(selectedScan.expense_vat_rate || selectedScan.expense_amount_ht) && (
                        <div style={{ marginBottom: '8px', paddingLeft: '8px', borderLeft: '2px solid #90caf9' }}>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            TVA: {selectedScan.expense_vat_rate ? `${parseFloat(selectedScan.expense_vat_rate)}%` : '-'}
                            {selectedScan.expense_vat_amount ? ` (${parseFloat(selectedScan.expense_vat_amount).toFixed(2)} €)` : ''}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            HT: {selectedScan.expense_amount_ht ? `${parseFloat(selectedScan.expense_amount_ht).toFixed(2)} €` : '-'}
                          </div>
                        </div>
                      )}
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Commerçant: </span>
                        <span style={isCorrected('merchant') ? correctedStyle : normalStyle}>
                          {selectedScan.expense_merchant || '-'}
                          {isCorrected('merchant') && correctedIcon}
                        </span>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Date: </span>
                        <span style={isCorrected('date') ? correctedStyle : normalStyle}>
                          {selectedScan.expense_date ? new Date(selectedScan.expense_date).toLocaleDateString('fr-FR') : '-'}
                          {isCorrected('date') && correctedIcon}
                        </span>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Ville: </span>
                        <span style={isCorrected('city') ? correctedStyle : normalStyle}>
                          {selectedScan.expense_location || '-'}
                          {isCorrected('city') && correctedIcon}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#666' }}>Type: </span>
                        <span style={normalStyle}>{selectedScan.expense_type || '-'}</span>
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {/* Donnees extraites OCR (brutes) */}
                {(selectedScan.ocr_extracted_data || selectedScan.ocr_parsed_json) && (() => {
                  const ocrData = selectedScan.ocr_extracted_data?.extracted || selectedScan.ocr_extracted_data || selectedScan.ocr_parsed_json || {};
                  return (
                  <div style={{ marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Données OCR brutes</h4>
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '16px',
                      fontSize: '12px'
                    }}>
                      {ocrData.amount != null && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ color: '#666' }}>Montant détecté: </span>
                          <span>{ocrData.amount} €</span>
                        </div>
                      )}
                      {ocrData.date && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ color: '#666' }}>Date détectée: </span>
                          <span>{ocrData.date}</span>
                        </div>
                      )}
                      {ocrData.merchant && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ color: '#666' }}>Commerçant détecté: </span>
                          <span>{ocrData.merchant}</span>
                        </div>
                      )}
                      {ocrData.city && (
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ color: '#666' }}>Ville détectée: </span>
                          <span>{ocrData.city}</span>
                        </div>
                      )}
                      {(ocrData.confidence || selectedScan.ocr_extracted_data?.confidence) && (
                        <div>
                          <span style={{ color: '#666' }}>Confiance: </span>
                          <span>{Math.round(ocrData.confidence || selectedScan.ocr_extracted_data?.confidence)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })()}

                {/* Bouton reanalyser */}
                {selectedScan.expense_id && (
                  <button
                    onClick={() => {
                      handleReanalyze(selectedScan.expense_id);
                      setSelectedScan(null);
                    }}
                    style={{
                      marginTop: '16px',
                      width: '100%',
                      padding: '12px',
                      background: '#1a1a2e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Relancer l'analyse OCR
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour saisir la remarque (incorrect) */}
      {noteModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#c62828' }}>
              Marquer comme incorrect
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
              Indiquez ce qui doit être corrigé dans ce scan OCR :
            </p>
            <textarea
              value={noteModal.note}
              onChange={(e) => setNoteModal(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Ex: Montant incorrect (détecté 51€ au lieu de 15€), date erronée..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '16px'
            }}>
              <button
                onClick={() => setNoteModal({ open: false, expenseId: null, note: '' })}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Annuler
              </button>
              <button
                onClick={submitIncorrectNote}
                disabled={!noteModal.note.trim() || updatingValidation}
                style={{
                  padding: '10px 20px',
                  background: noteModal.note.trim() ? '#c62828' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: noteModal.note.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {updatingValidation ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCRPanel;
